import React, { useState } from 'react';
import { BackLink } from '../components/BackLink';
import { BellIcon, CalendarIcon, ChevronDownIcon, SmartphoneIcon, CheckIcon, AlertCircleIcon, ArrowRightIcon } from 'lucide-react';
type NotificationFrequency = 'immediate' | 'daily' | 'weekly' | 'never';
interface NotificationSetting {
  id: string;
  name: string;
  description: string;
  frequency: NotificationFrequency;
}
export const NotificationsPage = () => {
  // Email notification settings
  const [emailSettings, setEmailSettings] = useState<NotificationSetting[]>([{
    id: 'event-recommendations',
    name: 'New Event Recommendations',
    description: 'Alerts about new events tailored to your interests',
    frequency: 'weekly'
  }, {
    id: 'price-drops',
    name: 'Price Drops & Ticket Alerts',
    description: 'Updates on saved events, like when tickets go on sale or prices change',
    frequency: 'immediate'
  }, {
    id: 'social-activity',
    name: 'Social Activity',
    description: 'When a friend invites you to a Circle or a Plan',
    frequency: 'immediate'
  }, {
    id: 'planning-updates',
    name: 'Planning Updates',
    description: 'Reminders and updates for events you are attending',
    frequency: 'daily'
  }, {
    id: 'lavee-news',
    name: 'LaVee.life News & Updates',
    description: 'Occasional updates about new platform features and company news',
    frequency: 'weekly'
  }]);
  // Calendar integration settings
  const [isCalendarConnected, setIsCalendarConnected] = useState(true);
  const [autoAddToCalendar, setAutoAddToCalendar] = useState(true);
  const connectedEmail = 'john.doe@example.com';
  // Handle frequency change for a notification setting
  const handleFrequencyChange = (id: string, frequency: NotificationFrequency) => {
    setEmailSettings(emailSettings.map(setting => setting.id === id ? {
      ...setting,
      frequency
    } : setting));
  };
  // Render frequency dropdown
  const renderFrequencyDropdown = (setting: NotificationSetting) => {
    const options: {
      value: NotificationFrequency;
      label: string;
    }[] = [{
      value: 'immediate',
      label: 'Immediate'
    }, {
      value: 'daily',
      label: 'Daily Digest'
    }, {
      value: 'weekly',
      label: 'Weekly Digest'
    }, {
      value: 'never',
      label: 'Never'
    }];
    return <div className="relative" data-id="element-1038">
        <select value={setting.frequency} onChange={e => handleFrequencyChange(setting.id, e.target.value as NotificationFrequency)} className="appearance-none w-full bg-[#2C2F33] border border-[#3A3D42] text-[#F5F5F7] py-2 px-4 pr-8 rounded-lg focus:outline-none focus:border-[#007BFF]" data-id="element-1039">
          {options.map(option => <option key={option.value} value={option.value} data-id="element-1040">
              {option.label}
            </option>)}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400" data-id="element-1041">
          <ChevronDownIcon size={16} data-id="element-1042" />
        </div>
      </div>;
  };
  return <div className="w-full min-h-screen bg-[#1A1A1D] text-[#F5F5F7]" data-id="element-1043">
      <div className="max-w-4xl mx-auto p-6" data-id="element-1044">
        <div className="mb-8" data-id="element-1045">
          <BackLink to="/app" className="mb-4" data-id="element-1046">
            Back to Dashboard
          </BackLink>
          <h1 className="text-3xl font-bold mb-2" data-id="element-1047">My Notifications</h1>
          <p className="text-gray-400" data-id="element-1048">
            Control how and when LaVee.life communicates with you. Customize
            your notification preferences to ensure you get the information that
            matters most to you.
          </p>
        </div>
        {/* Email Notifications Section */}
        <div className="bg-[#22252A] rounded-xl p-6 shadow-lg mb-6" data-id="element-1049">
          <div className="flex items-center mb-6" data-id="element-1050">
            <div className="w-10 h-10 bg-[#007BFF] bg-opacity-20 rounded-full flex items-center justify-center mr-3" data-id="element-1051">
              <BellIcon size={20} className="text-[#007BFF]" data-id="element-1052" />
            </div>
            <div data-id="element-1053">
              <h2 className="text-xl font-bold" data-id="element-1054">Email Notifications</h2>
              <p className="text-sm text-gray-400" data-id="element-1055">
                Choose how often you want to receive different types of emails
                from us
              </p>
            </div>
          </div>
          <div className="space-y-6" data-id="element-1056">
            {emailSettings.map(setting => <div key={setting.id} className="border-b border-[#3A3D42] pb-4 last:border-0 last:pb-0" data-id="element-1057">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1058">
                  <div data-id="element-1059">
                    <h3 className="font-medium" data-id="element-1060">{setting.name}</h3>
                    <p className="text-sm text-gray-400" data-id="element-1061">
                      {setting.description}
                    </p>
                  </div>
                  <div className="min-w-[160px]" data-id="element-1062">
                    {renderFrequencyDropdown(setting)}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
        {/* Calendar Integration Section */}
        <div className="bg-[#22252A] rounded-xl p-6 shadow-lg mb-6" data-id="element-1063">
          <div className="flex items-center mb-6" data-id="element-1064">
            <div className="w-10 h-10 bg-[#007BFF] bg-opacity-20 rounded-full flex items-center justify-center mr-3" data-id="element-1065">
              <CalendarIcon size={20} className="text-[#007BFF]" data-id="element-1066" />
            </div>
            <div data-id="element-1067">
              <h2 className="text-xl font-bold" data-id="element-1068">Calendar Integration</h2>
              <p className="text-sm text-gray-400" data-id="element-1069">
                Manage how events are added to your calendar
              </p>
            </div>
          </div>
          {isCalendarConnected ? <div className="bg-[#2C2F33] p-4 rounded-lg border border-[#3A3D42]" data-id="element-1070">
              <div className="flex items-center justify-between" data-id="element-1071">
                <div data-id="element-1072">
                  <h3 className="font-medium" data-id="element-1073">
                    Automatically add events to my calendar
                  </h3>
                  <p className="text-sm text-gray-400" data-id="element-1074">
                    When you save or get tickets for an event, we'll add it to
                    your connected calendar ({connectedEmail})
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-1075">
                  <input type="checkbox" checked={autoAddToCalendar} onChange={() => setAutoAddToCalendar(!autoAddToCalendar)} className="sr-only peer" data-id="element-1076" />
                  <div className="w-11 h-6 bg-[#3A3D42] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#007BFF]" data-id="element-1077"></div>
                </label>
              </div>
            </div> : <div className="bg-[#2C2F33] p-4 rounded-lg border border-[#3A3D42]" data-id="element-1078">
              <div className="flex items-center mb-4" data-id="element-1079">
                <AlertCircleIcon size={20} className="text-[#007BFF] mr-2" data-id="element-1080" />
                <h3 className="font-medium" data-id="element-1081">
                  Connect a calendar to enable this feature
                </h3>
              </div>
              <p className="text-sm text-gray-400 mb-4" data-id="element-1082">
                You need to connect a calendar first before you can enable
                automatic event adding.
              </p>
              <a href="/app/schedule" className="inline-flex items-center text-[#007BFF] hover:text-[#0056b3] transition-colors" data-id="element-1083">
                Go to Manage My Schedule
                <ArrowRightIcon size={16} className="ml-1" data-id="element-1084" />
              </a>
            </div>}
        </div>
        {/* Push Notifications Section */}
        <div className="bg-[#22252A] rounded-xl p-6 shadow-lg mb-6" data-id="element-1085">
          <div className="flex items-center mb-6" data-id="element-1086">
            <div className="w-10 h-10 bg-[#007BFF] bg-opacity-20 rounded-full flex items-center justify-center mr-3" data-id="element-1087">
              <SmartphoneIcon size={20} className="text-[#007BFF]" data-id="element-1088" />
            </div>
            <div data-id="element-1089">
              <h2 className="text-xl font-bold" data-id="element-1090">Push Notifications</h2>
              <p className="text-sm text-gray-400" data-id="element-1091">
                Get instant updates on the go
              </p>
            </div>
          </div>
          <div className="bg-gradient-to-r from-[#1A2030] to-[#2A3040] p-6 rounded-lg border border-[#3A3D42] text-center" data-id="element-1092">
            <SmartphoneIcon size={48} className="text-[#007BFF] mx-auto mb-4" data-id="element-1093" />
            <h3 className="text-xl font-bold mb-2" data-id="element-1094">
              Get instant updates on the go
            </h3>
            <p className="text-gray-400 mb-6" data-id="element-1095">
              Enable push notifications with our mobile app to get real-time
              alerts for the things that matter most.
            </p>
            <button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-colors inline-flex items-center" data-id="element-1096">
              Get the App
              <ArrowRightIcon size={16} className="ml-2" data-id="element-1097" />
            </button>
            <p className="text-xs text-gray-500 mt-4" data-id="element-1098">
              Available for iOS and Android devices
            </p>
          </div>
        </div>
        {/* Privacy Guarantee */}
        <div className="bg-[#2C2F33] p-4 rounded-lg border border-[#3A3D42]" data-id="element-1099">
          <div className="flex items-center" data-id="element-1100">
            <div className="w-10 h-10 bg-[#007BFF] bg-opacity-20 rounded-full flex items-center justify-center mr-3" data-id="element-1101">
              <CheckIcon size={20} className="text-[#007BFF]" data-id="element-1102" />
            </div>
            <div data-id="element-1103">
              <h3 className="font-medium" data-id="element-1104">Privacy Guarantee</h3>
              <p className="text-sm text-gray-400" data-id="element-1105">
                We respect your privacy. You can change your notification
                preferences or unsubscribe at any time.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};